import React from 'react';
import styled from '@emotion/styled';
import { scaleLinear } from 'd3-scale';
import { extent, max, bin, range, mean } from 'd3-array';
import { translate } from '~/core/scripts/chart-components/utils';
import { numberFormat } from '~/core/scripts/utils';

const HistoRect = styled.rect`
  fill: ${({ isHighlighted, theme }) =>
    isHighlighted ? theme.colors.grey20 : theme.colors.grey70};
`;

const TickLine = styled.line`
  stroke-width: 1;
  stroke: ${({ theme }) => theme.colors.grey40};
`;

const Label = styled.text`
  text-anchor: middle;
  font-family: ${({ theme }) => theme.fonts.sans};
  fill: ${({ theme }) => theme.colors.grey40};
  font-size: 12px;
`;

const margin = {
  top: 20,
  right: 20,
  bottom: 20,
  left: 20,
};

const size = {
  width: 200,
  height: 80,
};

const Histogram = ({ values, highlightValue, highlightLabel, decimals }) => {
  const width = size.width - margin.left - margin.right;
  const height = size.height - margin.top - margin.bottom;
  const barCount = 20;
  const xDomain = extent(values);
  const x = scaleLinear().domain(xDomain).range([0, width]).nice();
  const thresholds = range(
    xDomain[0],
    xDomain[1],
    (xDomain[1] - xDomain[0]) / (barCount - 1)
  );
  const bins = bin().domain(x.domain()).thresholds(thresholds)(values);
  const yMax = max(bins, (d) => d.length);
  const y = scaleLinear().domain([0, yMax]).range([height, 0]).nice();
  const barWidth = Math.floor(width / barCount);
  const minLabel = numberFormat(xDomain[0], decimals);
  const maxLabel = numberFormat(xDomain[1], decimals);
  const highlightBin = bins.find(
    (d) => highlightValue >= d.x0 && highlightValue < d.x1
  );
  const avg = mean(values);
  const avgBin = bins.find((d) => avg >= d.x0 && avg < d.x1);
  const avgX = x(avgBin.x0) + barWidth / 2;

  return (
    <div>
      <svg width={size.width} height={size.height}>
        <g transform={translate(margin.left, margin.top)}>
          {bins.map((d, i) => (
            <HistoRect
              key={`histo-rect-${i}`}
              x={i * barWidth}
              y={Math.round(y(d.length))}
              width={barWidth - 1}
              height={y(0) - Math.round(y(d.length)) + 2}
              isHighlighted={d.x0 === highlightBin.x0}
            />
          ))}
          <g transform={translate(0, height)}>
            <TickLine x1={0} x2={0} y1={0} y2={5} />
            <Label y={16}>{minLabel}</Label>
          </g>
          <g transform={translate(width, height)}>
            <TickLine x1={0} x2={0} y1={0} y2={5} />
            <Label y={16}>{maxLabel}</Label>
          </g>
          {avgX > 15 && avgX < width - 15 && (
            <g transform={translate(avgX, height)}>
              <TickLine x1={0} x2={0} y1={0} y2={5} />
              <Label y={16}>ø</Label>
            </g>
          )}
        </g>
      </svg>
    </div>
  );
};

export default Histogram;
